import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Modal, Field, Badge, Empty, Pagination, label } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { IconPlus } from '../components/icons.jsx';
import MultiSelect from '../components/MultiSelect.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const TYPES = ['manufacturer', 'client', 'own'];
export const TYPE_TONE = { manufacturer: 'blue', client: 'green', own: 'amber' };
const COUNTRIES = ['Italy', 'Morocco', 'UAE', 'Other'];
const blank = { name: '', type: 'client', country: 'Italy', contact_name: '', contact_email: '', contact_phone: '', address: '', notes: '' };
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const typeTemplate = (row) => (
  <Badge tone={TYPE_TONE[row.type] || 'gray'}>{label(row.type)}</Badge>
);

const contactTemplate = (row) => (
  <div>
    <div>{row.contact_name || '—'}</div>
    <div className="muted sm">{row.contact_email}</div>
  </div>
);

export default function Companies() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const search = searchParams.get('q') || '';
  const type = searchParams.get('type') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const patchParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (type) params.set('type', type);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/companies?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [search, type, page]);
  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  const columns = [
    { field: 'name', headerText: t('Name'), width: 200 },
    { headerText: t('Type'), width: 130, template: typeTemplate },
    { field: 'country', headerText: t('Country'), width: 110 },
    { headerText: t('Contact'), width: 200, template: contactTemplate },
  ];

  const hasFilters = !!(search || type);

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Companies')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename="companies"
            rows={list || []}
            columns={[
              { header: t('Name'), field: 'name' },
              { header: t('Type'), field: 'type' },
              { header: t('Country'), field: 'country' },
              { header: t('Contact'), field: 'contact_name' },
              { header: t('Email'), field: 'contact_email' },
              { header: t('Phone'), field: 'contact_phone' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => setEditing({ ...blank })}>
              <IconPlus width={15} height={15} /> {t('New company')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="search">
          <input
            placeholder={t('Search name or contact…')}
            value={search}
            onChange={(e) => patchParams({ q: e.target.value || null, page: null })}
          />
        </form>
        <select value={type} onChange={(e) => patchParams({ type: e.target.value || null, page: null })}>
          <option value="">{t('All types')}</option>
          {TYPES.map((ty) => <option key={ty} value={ty}>{label(ty)}</option>)}
        </select>
        {hasFilters && (
          <button type="button" className="btn ghost sm" onClick={() => patchParams({ q: null, type: null, page: null })}>
            {t('Clear')}
          </button>
        )}
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No companies yet. Add the first one with "New company".')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={(row) => navigate(`/companies/${row.id}`)}
          onRowAction={(row) => navigate(`/companies/${row.id}`)}
        />
      )}

      <Pagination meta={meta} onPage={(p) => patchParams({ page: p <= 1 ? null : String(p) })} />

      {editing && (
        <CompanyForm
          initial={editing}
          isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate('/companies'); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

export function CompanyForm({ initial, isAdmin, onClose, onSaved }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [form, setForm] = useState({ category_ids: [], ...initial });
  const [baseline, setBaseline] = useState(() => JSON.stringify({ category_ids: [], ...initial }));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [hasLogo, setHasLogo] = useState(!!initial.logo_stored);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInput = useRef(null);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get('/categories?domain=company_specialty').then((r) => setSpecialties(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`/companies/${initial.id}`).then((r) => {
        const ids = r.data.category_ids || [];
        setForm((f) => ({ ...f, category_ids: ids }));
        setBaseline((b) => JSON.stringify({ ...JSON.parse(b), category_ids: ids }));
        setHasLogo(!!r.data.logo_stored);
      }).catch(() => {});
    }
  }, [initial.id, isNew]);

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.upload(`/companies/${initial.id}/logo`, fd);
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
      invalidate('/companies');
      toast(t('Logo updated'));
    } catch (err) { toast(err.message, { tone: 'error' }); }
    finally { setLogoBusy(false); if (logoInput.current) logoInput.current.value = ''; }
  }

  async function removeLogo() {
    if (!(await confirm({ title: t('Remove logo'), message: t('Remove this company\'s logo?'), confirmLabel: t('Remove'), danger: true }))) return;
    setLogoBusy(true);
    try {
      await api.del(`/companies/${initial.id}/logo`);
      setHasLogo(false);
      invalidate('/companies');
      toast(t('Logo removed'));
    } catch (err) { toast(err.message, { tone: 'error' }); }
    finally { setLogoBusy(false); }
  }

  async function save() {
    setBusy(true); setErrors({});
    try {
      if (isNew) await api.post('/companies', form);
      else await api.put(`/companies/${initial.id}`, form);
      invalidate('/companies');
      toast(isNew ? t('Company created') : t('Company saved'));
      onSaved();
    } catch (err) {
      setErrors(err.fields || { _: err.message });
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm({ title: t('Delete company'), message: t('Delete this company?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/companies/${initial.id}`);
    invalidate('/companies');
    toast(t('Company deleted'));
    onSaved();
  }

  return (
    <Modal
      title={isNew ? t('New company') : t('Edit company')}
      onClose={onClose}
      dirty={JSON.stringify(form) !== baseline}
      footer={
        <div className="modal-actions">
          {!isNew && isAdmin && <button type="button" className="btn danger ghost" onClick={remove}>{t('Delete')}</button>}
          <div className="spacer" />
          <button type="button" className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button type="button" className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }
    >
      {errors._ && <div className="alert error">{errors._}</div>}

      {!isNew && (
        <div className="logo-upload">
          {hasLogo ? (
            <img src={`${API_BASE}/companies/${initial.id}/logo?v=${logoVersion}`} alt={t('Logo')} />
          ) : (
            <div className="logo-upload-placeholder">{t('No logo')}</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadLogo} />
            <button type="button" className="btn ghost sm" disabled={logoBusy} onClick={() => logoInput.current?.click()}>
              {hasLogo ? t('Replace logo') : t('Upload logo')}
            </button>
            {hasLogo && (
              <button type="button" className="btn ghost sm danger" disabled={logoBusy} onClick={removeLogo}>{t('Remove logo')}</button>
            )}
          </div>
        </div>
      )}

      <Field label={t('Name')} error={errors.name}><input value={form.name} onChange={set('name')} /></Field>
      <div className="grid-2">
        <Field label={t('Type')}><select value={form.type} onChange={set('type')}>{TYPES.map((ty) => <option key={ty} value={ty}>{label(ty)}</option>)}</select></Field>
        <Field label={t('Country')}><select value={form.country} onChange={set('country')}>{COUNTRIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}</select></Field>
      </div>
      <Field label={t('Contact name')}><input value={form.contact_name || ''} onChange={set('contact_name')} /></Field>
      <div className="grid-2">
        <Field label={t('Contact email')} error={errors.contact_email}><input value={form.contact_email || ''} onChange={set('contact_email')} /></Field>
        <Field label={t('Contact phone')}><input value={form.contact_phone || ''} onChange={set('contact_phone')} /></Field>
      </div>
      <Field label={t('Address (printed on invoices, multiline)')}>
        <textarea rows={3} value={form.address || ''} onChange={set('address')} placeholder={t('Street\nPostal code, City\nCountry')} />
      </Field>
      <MultiSelect label={t('Specialties / categories')} options={specialties} value={form.category_ids || []}
        onChange={(ids) => setForm((f) => ({ ...f, category_ids: ids }))} empty={t('No categories yet — add some in Settings')} />
      <Field label={t('Notes')}><textarea rows={3} value={form.notes || ''} onChange={set('notes')} /></Field>

      <div className="section-label" style={{ marginTop: 4 }}>{t('Invoicing details (optional)')}</div>
      <div className="grid-2">
        <Field label={t('Tax ID / P.IVA / ICE')}><input value={form.tax_id || ''} onChange={set('tax_id')} /></Field>
        <Field label={t('RC (trade register)')}><input value={form.legal_rc || ''} onChange={set('legal_rc')} /></Field>
      </div>
      {form.type === 'own' && (
        <>
          <div className="grid-2">
            <Field label={t('Tribunal')}><input value={form.legal_tribunal || ''} onChange={set('legal_tribunal')} /></Field>
            <Field label={t('Share capital')}><input value={form.legal_capital || ''} onChange={set('legal_capital')} /></Field>
          </div>
          <Field label={t('Activity')}><input value={form.legal_activite || ''} onChange={set('legal_activite')} /></Field>
          <div className="grid-2">
            <Field label={t('Legal representative')}><input value={form.legal_rep_name || ''} onChange={set('legal_rep_name')} /></Field>
            <Field label={t('Representative role')}><input value={form.legal_rep_role || ''} onChange={set('legal_rep_role')} /></Field>
          </div>
          <div className="grid-2">
            <Field label={t('Bank name')}><input value={form.bank_name || ''} onChange={set('bank_name')} /></Field>
            <Field label={t('IBAN')}><input value={form.bank_iban || ''} onChange={set('bank_iban')} /></Field>
          </div>
          <div className="grid-2">
            <Field label={t('SWIFT / BIC')}><input value={form.bank_swift || ''} onChange={set('bank_swift')} /></Field>
            <Field label={t('Account holder')}><input value={form.bank_titulaire || ''} onChange={set('bank_titulaire')} /></Field>
          </div>
        </>
      )}
    </Modal>
  );
}
